import { useEffect, useState } from "react";
import type { SessionInfo } from "@swift/shared";
import { api, ApiError } from "../../lib/api";

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleString("nl-NL", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return s;
  }
}

export default function SessionsTab() {
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function refresh() {
    try {
      const { sessions } = await api.listSessions();
      setSessions(sessions);
      setError(null);
    } catch {
      setError("Sessies kunnen niet worden opgehaald.");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function revoke(prefix: string) {
    setBusy(prefix);
    setError(null);
    try {
      await api.revokeSession(prefix);
      setSessions((prev) => (prev ? prev.filter((s) => s.prefix !== prefix) : prev));
    } catch (err) {
      if (err instanceof ApiError && err.code === "cannot_revoke_current") {
        setError("Je eigen huidige sessie kun je niet revoken — gebruik daarvoor Uitloggen.");
      } else {
        setError("Revoke mislukt.");
      }
    } finally {
      setBusy(null);
    }
  }

  if (sessions === null) {
    return <div className="px-4 py-4 text-sm text-slate-400">Laden…</div>;
  }

  return (
    <div className="px-4 py-3">
      <p className="mb-3 text-xs text-slate-400">
        Apparaten waarop je momenteel bent ingelogd. Revoke een sessie om dat
        apparaat uit te loggen — handig na verlies of na inloggen op de PC van
        een collega.
      </p>
      {error && (
        <div className="mb-3 rounded border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-200">
          {error}
        </div>
      )}
      <ul className="divide-y divide-slate-700 rounded border border-slate-700">
        {sessions.map((s) => (
          <li
            key={s.prefix}
            className="flex items-center gap-3 px-3 py-2 text-sm"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-slate-200">{s.device_label}</span>
                {s.is_current && (
                  <span className="rounded-full bg-brand-green/20 px-2 py-0.5 text-[10px] font-semibold text-brand-green">
                    Dit apparaat
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500">
                Laatst gezien {formatDate(s.last_seen)} · ingelogd op {formatDate(s.created_at)}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-600">
                id {s.prefix}…
              </div>
            </div>
            {!s.is_current && (
              <button
                onClick={() => void revoke(s.prefix)}
                disabled={busy === s.prefix}
                className="shrink-0 rounded border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:bg-red-900/40 hover:text-red-300 disabled:opacity-50 sm:px-2 sm:py-1"
              >
                {busy === s.prefix ? "…" : "Revoke"}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
